import React, { useMemo } from 'react'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Button, WandIcon } from '@demandlocal/components'
import CustomersSources from './CustomersSources'
import LoadingAnimation from '../../../../components/LoadingAnimation'
import { ReactComponent as AddCustomersImage } from '../../../../images/add_customers.svg'
import AddCustomersForm from '../../../../forms/AddCustomers'
import { ReactComponent as ChevronRight } from '../../../../images/icons/chevron_right.svg'
import { useNavigate } from 'react-router'
import CustomerSourceDetails from './CustomerSourceDetails'
import { BuildAudienceGuide } from '../../../../components/Audiences/BuildAudienceGuide'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import { useTranslation } from 'react-i18next'
import CustomersList from './List'
import Modal from '../../../../components/Modal/Modal'
import Customer from './Customer/Customer'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import { ReactComponent as CustomersIcon } from '../../../../images/icons/customers.svg'

const DealerCustomersTemplate = (): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const {
    data: {
      totalCustomers = 0,
      totalAudiences = 0,
      sources = {
        total: 0,
      },
    } = {},
    isLoading: isLoadingOverallStats,
  } = useGetOverallStats()

  const navigate = useNavigate()
  const isLoading = useMemo(() => isLoadingOverallStats, [isLoadingOverallStats])

  const showCustomers = useMemo(
    () => sources.total > 0 || agency?.is_tier_2,
    [agency?.is_tier_2, sources.total],
  )

  if (isLoading) {
    return (
      <div className={'flex flex-1 flex-col h-full'}>
        <div className={'flex flex-col my-auto mx-auto items-center justify-center'}>
          <LoadingAnimation />
        </div>
      </div>
    )
  }

  return (
    <div className={'flex flex-1 flex-col h-full'}>
      {showCustomers ? (
        <div>
          <PageHeader
            icon={
              <div className="flex justify-center items-center rounded-full w-12 h-12 bg-black">
                <CustomersIcon />
              </div>
            }
            title={'Customers'}
            tabMenu={[
              { name: 'List', to: 'list' },
              { name: 'Sources', to: 'sources/list' },
            ]}
            rightSlot={({ hidden }) => (
              <div className="flex flex-row gap-2 items-center">
                {!hidden && (
                  <div className="flex flex-row items-center gap-2">
                    <div className="flex justify-center items-center bg-primary-persian-blue/10 rounded-full w-[32px] h-[32px]">
                      <WandIcon />
                    </div>
                    <span className="font-bold text-xs text-[#7888C0]">
                      {totalAudiences > 0
                        ? 'Great you have an audience! Use your audience in a marketing campaign'
                        : totalCustomers > 0
                        ? 'Great you have customers! Use your customers in an audience'
                        : ''}
                    </span>
                    <ChevronRight className="stroke-[#7888C0] fill-[#7888C0]" />
                  </div>
                )}
                <Button
                  collapsed={hidden}
                  leftSlot={'+'}
                  variant="rainbow"
                  onClick={() => {
                    if (totalAudiences > 0)
                      navigate(t('dealership.campaigns.create', { dealership }))
                    else if (totalCustomers > 0)
                      navigate(t('dealership.audiences.create', { dealership }))
                    else
                      navigate(t('dealership.customers.sources.create', { dealership }))
                  }}>
                  {totalAudiences > 0
                    ? 'Create Campaign'
                    : totalCustomers > 0
                    ? 'Create Audience'
                    : 'Create Customers'}
                </Button>
              </div>
            )}
          />
          <div className={'flex flex-1 flex-col max-w-[1920px] mx-auto w-full'}>
            <div className={'mb-10 pt-12'}>
              <Outlet />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={'flex flex-col gap-5 my-auto mx-auto items-center justify-center'}>
          <AddCustomersImage className="mx-auto" />
          <span className={'font-bold text-3xl font-Spartan'}>
            You have no customers yet
          </span>
          <span className={'text-base font-Spartan'}>
            How adding customers can help you reach your target audience
          </span>
          <div className={'w-2/3'}>
            <BuildAudienceGuide />
          </div>
          <Button
            variant="rainbow"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            + Add customers
          </Button>
          <Outlet />
        </div>
      )}
    </div>
  )
}

const DealerCustomers = (): JSX.Element => {
  const dealership = useDealership()
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const {
    data: {
      sources = {
        total: 0,
      },
    } = {},
    isLoading,
  } = useGetOverallStats()

  return (
    <Routes>
      <Route
        path="/sources/details/:customer_source_id"
        element={<CustomerSourceDetails />}
      />
      <Route element={<DealerCustomersTemplate />}>
        <Route path={'/list/*'} element={<CustomersList />}>
          <Route
            path={':customer_id/*'}
            element={
              <Modal
                isOpen={true}
                modalRoute={true}
                key={'CustomerDetails'}
                htmlOptions={{
                  maxWidth: 1024,
                }}>
                <Customer />
              </Modal>
            }></Route>
        </Route>
        <Route path="sources/*">
          <Route
            path={'list'}
            element={
              !isLoading && (sources?.total > 0 || agency?.is_tier_2) ? (
                <CustomersSources />
              ) : null
            }>
            <Route
              path={'create/*'}
              element={<AddCustomersForm />}
              key={'add_customers_route'}
            />
          </Route>
          <Route path="*" element={<Navigate to="list" replace />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default DealerCustomers
